import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Alert,
} from '@mui/material';
import { Timer, ArrowBack, ArrowForward, Send } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import {
  StandaloneExamAttempt,
  StandaloneQuestion,
  StandaloneChoice,
} from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { toast } from 'react-hot-toast';

const sanitizeMathHtml = (html: string) =>
  html.replace(
    /<span[^>]*class=["'][^"']*note-latex[^"']*["'][^>]*>[\s\S]*?<\/span>/gi,
    ''
  );

const TakeStandaloneExam: React.FC = () => {
  const { hashed } = useParams<{ hashed: string }>();
  const navigate = useNavigate();

  const [attempt, setAttempt] = useState<StandaloneExamAttempt | null>(null);
  const [questions, setQuestions] = useState<StandaloneQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [attemptLimitReached, setAttemptLimitReached] = useState(false);
  const [attemptLimitDetail, setAttemptLimitDetail] = useState('');

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const autoSubmitFiredRef = useRef(false);

  useEffect(() => {
    const startExamSession = async () => {
      if (!hashed) return;

      try {
        setLoading(true);
        setAccessDenied(false);
        const examMeta = await apiService.getStandaloneExam(hashed);
        // Business rule: certificate-granting standalone exams cannot be retaken after pass.
        if (examMeta.grants_certificate) {
          const attempts = await apiService.getMyStandaloneExamAttemptsForExam(examMeta.id);
          const passedAttempt = attempts
            .filter((a) => a.submitted_at && a.is_passed)
            .sort(
              (a, b) =>
                new Date(b.submitted_at as string).getTime() -
                new Date(a.submitted_at as string).getTime()
            )[0];
          if (passedAttempt) {
            toast('You already passed this certificate exam. Showing your result.');
            navigate(`/exams/${hashed}/result/${passedAttempt.id}`, { replace: true });
            return;
          }
        }

        const { attempt: attemptData, questions: questionsData } = await apiService.startStandaloneExam(hashed);
        setAttempt(attemptData);
        setQuestions(questionsData);

        if (attemptData.user_answers && attemptData.user_answers.length > 0) {
          const savedAnswers: Record<number, number> = {};
          attemptData.user_answers.forEach((answer: any) => {
            const qId = answer.standalone_question?.id;
            if (qId != null && answer.selected_choice_id) {
              savedAnswers[qId] = answer.selected_choice_id;
            }
          });
          setSelectedAnswers(savedAnswers);
          const lastAnsweredIndex = attemptData.question_order.findIndex(
            (qId: number) => !savedAnswers[qId]
          );
          const resumeIndex =
            lastAnsweredIndex === -1
              ? attemptData.question_order.length - 1
              : Math.max(0, lastAnsweredIndex);
          setCurrentQuestionIndex(resumeIndex);
          toast.success(
            `Resuming... (Question ${resumeIndex + 1}/${attemptData.question_order.length})`
          );
        }

        const exam = attemptData.standalone_exam;
        if (exam?.duration_minutes) {
          const durationSeconds = exam.duration_minutes * 60;
          const startTime = new Date(attemptData.started_at).getTime();
          const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
          const remainingSeconds = Math.max(0, durationSeconds - elapsedSeconds);
          setTimeLeft(remainingSeconds);
          // timeLeft=0 will trigger the timer useEffect which calls handleAutoSubmit
        }
      } catch (err: any) {
        if (err?.response?.status === 403) {
          const errCode = err?.response?.data?.error;
          if (errCode === 'attempt_limit_reached') {
            setAttemptLimitReached(true);
            setAttemptLimitDetail(err?.response?.data?.detail || 'You have used all attempts for this exam.');
          } else {
            setAccessDenied(true);
          }
        } else {
          console.error('Error starting standalone exam:', err);
          toast.error('Could not start exam. Please try again.');
          navigate(`/exams/${hashed}`);
        }
      } finally {
        setLoading(false);
      }
    };

    startExamSession();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [hashed]);

  useEffect(() => {
    if (timeLeft !== null && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => (prev !== null ? prev - 1 : null));
      }, 1000);
    } else if (timeLeft === 0) {
      handleAutoSubmit();
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  const handleAnswerSelect = async (questionId: number, choiceId: number) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: choiceId }));
    if (attempt) {
      try {
        await apiService.submitStandaloneAnswer(attempt.id, questionId, choiceId);
      } catch (error) {
        console.error('Error saving answer:', error);
        toast.error('Failed to save answer. Please try again.');
      }
    }
  };

  const currentQuestion = questions[currentQuestionIndex];
  const safeQuestionHtml = currentQuestion ? sanitizeMathHtml(currentQuestion.question_text || '') : '';

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleAutoSubmit = async () => {
    if (autoSubmitFiredRef.current) return;
    autoSubmitFiredRef.current = true;
    toast.error('Time is up! Submitting your exam automatically.');
    await submitExam();
  };

  const submitExam = async () => {
    if (!attempt || submitting) return;
    setSubmitting(true);
    try {
      // Best-effort: re-sync locally selected answers before final submit.
      // Individual failures (e.g. time_limit_exceeded) are silently ignored —
      // answers already saved during the session are used for scoring.
      await Promise.all(
        Object.entries(selectedAnswers).map(([qId, cId]) =>
          apiService.submitStandaloneAnswer(attempt.id, parseInt(qId), cId).catch(() => null)
        )
      );
      await apiService.submitStandaloneExam(attempt.id);
      toast.success('Exam submitted successfully!');
      navigate(`/exams/${hashed}/result/${attempt.id}`);
    } catch (error: any) {
      console.error('Error submitting exam:', error);
      const status = error?.response?.status;
      const errMsg = error?.response?.data?.error;
      if (status === 400 && errMsg === 'Exam already submitted') {
        toast.success('Exam was already submitted. Showing your result.');
        navigate(`/exams/${hashed}/result/${attempt.id}`);
      } else {
        toast.error('Failed to submit exam. Please try again.');
      }
    } finally {
      setSubmitting(false);
      setIsSubmitDialogOpen(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) return <LoadingSpinner message="Preparing your exam questions..." />;
  if (attemptLimitReached) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {attemptLimitDetail}
        </Alert>
        <Button variant="contained" onClick={() => navigate(`/exams/${hashed}`)}>
          Back to Exam
        </Button>
      </Container>
    );
  }
  if (accessDenied) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          You need to purchase access to this exam before you can take it. After your purchase is
          verified, you can start the exam here.
        </Alert>
        <Button variant="contained" onClick={() => navigate(`/exams/${hashed}`)}>
          Back to Exam
        </Button>
      </Container>
    );
  }
  if (!attempt || questions.length === 0) return <Alert severity="error">No questions found for this exam.</Alert>;

  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const exam = attempt.standalone_exam;

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      {/* Header Info */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {exam.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Question {currentQuestionIndex + 1} of {questions.length}
          </Typography>
        </Box>
        {timeLeft !== null && (
          <Box sx={{ 
            p: 1.5, 
            px: 3, 
            borderRadius: 3, 
            backgroundColor: timeLeft < 60 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
            border: `1px solid ${timeLeft < 60 ? '#ef4444' : '#10b981'}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}>
            <Timer sx={{ color: timeLeft < 60 ? '#ef4444' : '#10b981' }} />
            <Typography variant="h5" sx={{ fontWeight: 800, color: timeLeft < 60 ? '#ef4444' : '#10b981', fontFamily: 'monospace' }}>
              {formatTime(timeLeft)}
            </Typography>
          </Box>
        )}
      </Box>

      <LinearProgress 
        variant="determinate" 
        value={progress} 
        sx={{ 
          height: 8, 
          borderRadius: 4, 
          mb: 4,
          backgroundColor: 'rgba(0,0,0,0.05)',
          '& .MuiLinearProgress-bar': {
            background: 'linear-gradient(45deg, #10b981 30%, #22c55e 90%)',
          }
        }} 
      />

      {/* Question Card */}
      <Card sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.1)', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', overflow: 'visible', position: 'relative' }}>
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          {/* Render question text as HTML (supports Summernote formatting) */}
          <Box 
            sx={{ 
              mb: 4,
              '& p': { 
                fontSize: '1.25rem',
                fontWeight: 400,
                lineHeight: 1.6,
                marginBottom: '0.5rem',
                color: 'text.primary',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              },
              '& img': {
                maxWidth: '100% !important',
                width: 'auto !important',
                height: 'auto !important',
                borderRadius: '8px',
                marginTop: '1rem',
                marginBottom: '1rem',
                display: 'block',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              },
              '& figure': {
                maxWidth: '100%',
                margin: '1rem 0',
              },
              '& table': {
                display: 'block',
                maxWidth: '100%',
                overflowX: 'auto',
              },
              '& ul, & ol': {
                paddingLeft: '1.5rem',
                marginBottom: '1rem'
              },
              '& li': {
                marginBottom: '0.5rem'
              },
              '& strong': {
                fontWeight: 700
              },
              '& em': {
                fontStyle: 'italic'
              },
              '& .katex-display': {
                maxWidth: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
              }
            }}
            dangerouslySetInnerHTML={{ __html: safeQuestionHtml }}
          />

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={selectedAnswers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswerSelect(currentQuestion.id, parseInt(e.target.value))}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(currentQuestion.choices || []).map((choice: StandaloneChoice) => (
                  <Paper
                    key={choice.id}
                    elevation={0}
                    sx={{
                      p: 1,
                      px: 2,
                      border: '1px solid',
                      borderColor: selectedAnswers[currentQuestion.id] === choice.id ? '#10b981' : 'rgba(0,0,0,0.1)',
                      backgroundColor: selectedAnswers[currentQuestion.id] === choice.id ? '#10b981' : 'white',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      width: '100%',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      '&:hover': {
                        borderColor: '#10b981',
                        backgroundColor: '#10b981',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                        '& *': {
                          color: '#ffffff !important',
                        }
                      }
                    }}
                  >
                    <FormControlLabel
                      value={choice.id}
                      control={<Radio color="success" sx={{ color: selectedAnswers[currentQuestion.id] === choice.id ? '#ffffff' : 'inherit' }} />}
                      label={
                        <Box 
                          sx={{ 
                            width: '100%',
                            maxWidth: '100%',
                            color: selectedAnswers[currentQuestion.id] === choice.id ? '#ffffff' : '#1e293b',
                            '& *': { 
                              color: selectedAnswers[currentQuestion.id] === choice.id ? '#ffffff !important' : '#1e293b !important',
                            },
                            '& p': { 
                              margin: 0,
                              fontSize: '1.05rem',
                              fontWeight: 400,
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                            },
                            '& strong': {
                              fontWeight: 700
                            },
                            '& img': {
                              maxWidth: '100% !important',
                              width: 'auto !important',
                              height: 'auto !important',
                              borderRadius: '6px',
                              marginTop: '0.5rem',
                              marginBottom: '0.5rem',
                              display: 'block'
                            },
                            '& figure': {
                              maxWidth: '100%',
                              margin: '0.5rem 0',
                            },
                            '& table': {
                              display: 'block',
                              maxWidth: '100%',
                              overflowX: 'auto',
                            },
                            '& .katex-display': {
                              maxWidth: '100%',
                              overflowX: 'auto',
                              overflowY: 'hidden',
                            }
                          }}
                          dangerouslySetInnerHTML={{ __html: sanitizeMathHtml(choice.choice_text || '') }}
                        />
                      }
                      sx={{ 
                        width: '100%', 
                        m: 0,
                        alignItems: 'flex-start',
                        maxWidth: '100%',
                        '& .MuiFormControlLabel-label': { 
                          width: '100%',
                          minWidth: 0,
                          pt: 0.5
                        }
                      }}
                    />
                  </Paper>
                ))}
              </Box>
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          disabled={currentQuestionIndex === 0}
          variant="outlined"
          sx={{ px: 4, borderRadius: 2, height: 48 }}
        >
          Previous
        </Button>

        {currentQuestionIndex === questions.length - 1 ? (
          <Button
            variant="contained"
            color="success"
            endIcon={<Send />}
            onClick={() => setIsSubmitDialogOpen(true)}
            sx={{ 
              px: 6, 
              borderRadius: 2, 
              height: 48,
              background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
              fontWeight: 700,
              boxShadow: '0 8px 16px rgba(16, 185, 129, 0.2)',
            }}
          >
            Review & Submit
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={handleNext}
            sx={{ px: 4, borderRadius: 2, height: 48 }}
          >
            Next Question
          </Button>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog 
        open={isSubmitDialogOpen} 
        onClose={() => setIsSubmitDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>
          Finish Exam?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" textAlign="center">
            You have answered {Object.keys(selectedAnswers).length} out of {questions.length} questions.
          </Typography>
          {Object.keys(selectedAnswers).length < questions.length && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Wait! You still have unanswered questions.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
          <Button onClick={() => setIsSubmitDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2, px: 3 }}>
            Keep Working
          </Button>
          <Button 
            onClick={submitExam} 
            variant="contained" 
            color="success" 
            disabled={submitting}
            sx={{ borderRadius: 2, px: 4, fontWeight: 700 }}
          >
            {submitting ? 'Submitting...' : 'Yes, Submit Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TakeStandaloneExam;
